import { create } from 'zustand';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { api, tokenBridge } from '@/lib/api';

export interface SessionUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
  plan: string;
  timezone?: string;
}

interface AuthState {
  ready: boolean;
  user: SessionUser | null;
  accessToken: string | null;
  /** In-flight refresh promise used by the axios interceptor (single-flight). */
  refreshPromise: Promise<boolean> | null;
  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, referralCode?: string) => Promise<void>;
  updateProfile: (patch: { name?: string; timezone?: string }) => Promise<void>;
  logout: () => Promise<void>;
  tryRefresh: () => Promise<boolean>;
}

const TOKEN_KEY = 'ai.refreshToken';

async function persistRefresh(token: string | null): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    if (token) await Preferences.set({ key: TOKEN_KEY, value: token });
    else await Preferences.remove({ key: TOKEN_KEY });
  } else if (token) {
    window.localStorage.setItem(TOKEN_KEY, token);
  } else {
    window.localStorage.removeItem(TOKEN_KEY);
  }
}

async function loadRefresh(): Promise<string | null> {
  if (Capacitor.isNativePlatform()) {
    const { value } = await Preferences.get({ key: TOKEN_KEY });
    return value;
  }
  return window.localStorage.getItem(TOKEN_KEY);
}

async function applySession(payload: {
  user: SessionUser;
  accessToken: string;
  refreshToken: string;
}): Promise<void> {
  await persistRefresh(payload.refreshToken);
  useAuthStore.setState({ user: payload.user, accessToken: payload.accessToken });
}

export const useAuthStore = create<AuthState>((set, get) => ({
  ready: false,
  user: null,
  accessToken: null,
  refreshPromise: null,

  bootstrap: async () => {
    try {
      const refreshToken = await loadRefresh();
      if (refreshToken) {
        const ok = await get().tryRefresh();
        if (!ok) await persistRefresh(null);
      }
      if (get().accessToken) {
        const { data } = await api.get<{ user: SessionUser }>('/auth/me');
        set({ user: data.user });
      }
    } catch {
      set({ user: null, accessToken: null });
      await persistRefresh(null);
    } finally {
      set({ ready: true });
    }
  },

  login: async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    await applySession(data);
  },

  register: async (name, email, password, referralCode) => {
    const { data } = await api.post('/auth/register', { name, email, password, referralCode });
    await applySession(data);
  },

  updateProfile: async (patch) => {
    const { data } = await api.patch('/auth/me', patch);
    set({ user: data.user });
  },

  logout: async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* server-side revocation is best-effort */
    }
    await persistRefresh(null);
    set({ user: null, accessToken: null });
  },

  tryRefresh: async () => {
    const inFlight = get().refreshPromise;
    if (inFlight) return inFlight;
    const promise = (async () => {
      try {
        const refreshToken = await loadRefresh();
        if (!refreshToken) return false;
        const { data } = await api.post('/auth/refresh', { refreshToken });
        await applySession(data);
        return true;
      } catch {
        await persistRefresh(null);
        set({ user: null, accessToken: null });
        return false;
      } finally {
        set({ refreshPromise: null });
      }
    })();
    set({ refreshPromise: promise });
    return promise;
  },
}));

/** Wire the axios interceptor bridge (avoids a circular import in api.ts). */
tokenBridge.get = () => useAuthStore.getState().accessToken;
tokenBridge.refresh = () => useAuthStore.getState().tryRefresh();
tokenBridge.onUnauthorized = () => {
  useAuthStore.setState({ user: null, accessToken: null });
  void persistRefresh(null);
  window.dispatchEvent(new CustomEvent('ai:session-expired'));
};
